import WolfStepIndicator from '@components/ui/WolfStepIndicator';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from '@hooks/useTranslation';
import { StepType } from '@models/step.model';
import { UserRoles } from '@models/user.model';
import { Add, DragIndicatorOutlined, ShareOutlined } from '@mui/icons-material';
import { Box, Icon, Paper, styled, Typography } from '@mui/material';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { useState } from 'react';
import { EditIcon20 } from './WolfIcons';
import WolfTooltip from './WolfTooltip';

interface WolfCardsStepProps {
  item: {
    stepId: string;
    name: string;
    position: string;
    type: StepType | null;
    order: number;
  };
  index: number;
  substep?: boolean;
  isLast?: boolean;
  selected?: boolean;
  drawLine?: boolean;
  dragDisabled?: boolean;
  isEditing?: boolean;
  isExecuting?: boolean;
  isUnsaved?: boolean;
  hasUnsavedChanges?: boolean;
  editingTitle?: string;
  onClick?: (step: any) => void;
  onEdit?: () => void;
  onShare?: () => void;
  onSubAdd?: () => void;
  canAddSubstep?: boolean;
  isDraggingGlobal?: boolean;
  isRecentlyDropped?: boolean;
}

interface StyledPaperProps {
  selected: boolean;
  step?: 'sub' | '';
  last?: 'last' | '';
  draw?: 'draw' | '';
  dragDisabled?: boolean;
  isRecentlyDropped?: boolean;
}

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => !['selected', 'step', 'last', 'draw', 'dragDisabled', 'isRecentlyDropped'].includes(prop as string),
})<StyledPaperProps>(({ theme, selected, step, last, draw, dragDisabled, isRecentlyDropped }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: step === 'sub' ? '18.5rem' : '20rem',
  minWidth: step === 'sub' ? '17rem' : '19rem',
  maxWidth: step === 'sub' ? '17rem' : '19rem',
  height: '4rem',
  minHeight: '4rem',
  padding: '0.75rem 1rem',
  borderRadius: '0.375rem',
  backgroundColor: isRecentlyDropped ? 'rgba(33, 150, 243, 0.15)' : 'white',
  border: `${selected ? 3 : 1}px solid ${selected ? theme.palette.callToAction.main : theme.palette.primaryFrozen.main}`,
  boxShadow: theme.shadows[1],
  margin: '0.5rem 0',
  position: 'relative',
  cursor: dragDisabled ? 'pointer' : 'grab',
  '&:hover': {
    border: `2px solid ${theme.palette.primary.main}`,
  },
  '&:active': {
    cursor: dragDisabled ? 'pointer' : 'grabbing',
  },
  transition: 'all 0.3s ease',
  '&::before': {
    content: step === 'sub' && draw === 'draw' ? '""' : 'none',
    position: 'absolute',
    left: '-1rem',
    top: -10,
    bottom: 0,
    width: '1px',
    height: last === 'last' ? '70%' : '140%',
    backgroundColor: theme.palette.primaryIcy.main,
  },
  '&::after': {
    content: step === 'sub' && draw === 'draw' ? '""' : 'none',
    position: 'absolute',
    left: '-1rem',
    top: '50%',
    width: '16px',
    height: '1px',
    backgroundColor: theme.palette.primaryIcy.main,
  },
}));

const TextContainer = styled(Typography)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  display: '-webkit-box',
  lineHeight: '1.3',
  maxHeight: '2.6em',
  wordBreak: 'break-word',
}));

const WolfCardsStep: React.FC<WolfCardsStepProps> = ({
  item,
  index,
  substep = false,
  isLast = false,
  selected = false,
  drawLine = false,
  dragDisabled = false,
  isEditing = false,
  isExecuting = false,
  isUnsaved = false,
  hasUnsavedChanges = false,
  editingTitle,
  onClick,
  onEdit,
  onShare,
  onSubAdd,
  canAddSubstep = true,
  isDraggingGlobal = false,
  isRecentlyDropped = false,
}) => {
  const { role } = useAppSelector((state: RootState) => state.user);

  const [activeHover, setActiveHover] = useState(false);
  const localeCommon = useTranslation('common');

  const isSwitchRepeat = item.type === StepType.SWITCH || item.type === StepType.REPEAT;
  const getDisplayName = () => {
    const titleToShow = editingTitle !== undefined ? editingTitle : item?.name;
    if (isUnsaved) return `(${localeCommon['unsaved']}) ${titleToShow}`;
    if (hasUnsavedChanges) return `(${localeCommon['withChanges']}) ${titleToShow}`;
    return titleToShow;
  };
  const displayName = getDisplayName();

  const handleHover = () => {
    setActiveHover(true);
  };

  const handleLeave = () => {
    setActiveHover(false);
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.stepId || index.toString(),
    disabled: dragDisabled || role === UserRoles.USER,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
    touchAction: 'none',
  };

  // Spread drag listeners only if not disabled
  const dragHandlers = (dragDisabled || role === UserRoles.USER) ? {} : { ...attributes, ...listeners };

  return (
    <Box position="relative" ref={setNodeRef} style={style} {...dragHandlers}>
      <StyledPaper
        onMouseEnter={handleHover}
        onMouseLeave={handleLeave}
        selected={selected}
        last={isLast ? 'last' : ''}
        draw={drawLine ? 'draw' : ''}
        step={substep ? 'sub' : ''}
        dragDisabled={dragDisabled}
        isRecentlyDropped={isRecentlyDropped}>
        <Box className="w-1/5 flex items-center justify-center mr-2" onClick={(e) => { e.stopPropagation(); if (onClick) onClick(item); }}>
          <WolfStepIndicator text={item?.position} type={item.type} isSub={substep} />
        </Box>
        <Box className={`flex items-center transition-all duration-200 ${(activeHover && isSwitchRepeat && !substep) ? 'w-2/5' : 'w-3/5'}`} onClick={(e) => { e.stopPropagation(); if (onClick) onClick(item); }}>
          {isDraggingGlobal ? (
            <TextContainer variant="body12" className="w-full">
              {displayName}
            </TextContainer>
          ) : (
            <WolfTooltip title={displayName}>
              <TextContainer variant="body12" className="w-full">
                {displayName}
              </TextContainer>
            </WolfTooltip>
          )}
        </Box>
        <Box className={`flex justify-end items-center ${(activeHover && isSwitchRepeat) ? "w-2/5" : "w-1/5"}`}>
          {activeHover &&
            (dragDisabled || role === UserRoles.USER ? (
              <Box className="flex justify-between items-center gap-0.5 text-sm">
                {!isEditing && !isExecuting && (
                  <Icon onClick={onEdit} className="!w-5 !h-5 hover:bg-primaryFrozen">
                    <EditIcon20 sx={{ fontSize: 20 }} />
                  </Icon>
                )}
                <Icon onClick={onShare} className="!w-5 !h-5 hover:bg-primaryFrozen">
                  <ShareOutlined sx={{ fontSize: 20 }} />
                </Icon>
              </Box>
            ) : (
              <Box className="flex justify-between items-center gap-0.5 text-sm">
                <Icon onClick={onShare} className="!w-5 !h-5 hover:bg-primaryFrozen">
                  <ShareOutlined sx={{ fontSize: 20 }} />
                </Icon>
                {!isEditing && !isExecuting && (
                  <Icon onClick={onEdit} className="!w-5 !h-5 hover:bg-primaryFrozen">
                    <EditIcon20 sx={{ fontSize: 20 }} />
                  </Icon>
                )}
                <Icon className="!w-5 !h-5 hover:bg-primaryFrozen">
                  <DragIndicatorOutlined sx={{ fontSize: 20 }} />
                </Icon>
              </Box>
            ))}
          <Box className="flex items-center text-sm">
            {isSwitchRepeat && !substep && canAddSubstep && role !== UserRoles.USER && (
              <WolfTooltip
                title={
                  localeCommon['add'] +
                  ' ' +
                  localeCommon['step'] +
                  ' ' +
                  localeCommon['inside'] +
                  ' ' +
                  item.name
                }>
                <Icon onClick={onSubAdd} className="!w-5 !h-5 hover:bg-primaryFrozen">
                  <Add sx={{ fontSize: 20 }} />
                </Icon>
              </WolfTooltip>
            )}
          </Box>
        </Box>
      </StyledPaper>
    </Box>
  );
};

export default WolfCardsStep;
